import { useMemo, useState } from 'react';
import { CardPrazo } from '../components/CardPrazo';
import { ModalDetalhe } from '../components/ModalDetalhe';
import { BuscaProcesso } from '../components/BuscaProcesso';
import { PageShell } from '../components/PageShell';
import { ehAdmin, useMe, usePrazosVariasSemanas } from '../hooks';
import type { PrazoCard } from '../types';
import { semanaAdjacente, semanaISO } from '../utils';

type Filtro = 'todos' | 'acao_necessaria' | 'aguardando' | 'concluidos';

export function MinhaCaixa() {
  const { data: me } = useMe();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [detalheId, setDetalheId] = useState<number | null>(null);

  // 4 semanas (atual ± 1 ± 2)
  const semanas = useMemo(() => {
    const atual = semanaISO();
    return [
      semanaAdjacente(atual, -1),
      atual,
      semanaAdjacente(atual, 1),
      semanaAdjacente(semanaAdjacente(atual, 1), 1),
    ];
  }, []);

  const { data } = usePrazosVariasSemanas(semanas);

  const todos = useMemo<PrazoCard[]>(() => {
    if (!data) return [];
    return data.flatMap((s) => s.colunas.flatMap((c) => c.cards));
  }, [data]);

  const relevantes = useMemo<PrazoCard[]>(() => {
    if (!me) return [];
    // Modelo 2-papéis (overhaul v4): colaborador (Maria) tem a caixa focada
    // em PRONTO P/ PROTOCOLO + protocolados; admin (Marcelo/Jorge) vê triagem,
    // seus cards e os prontos.
    const souColaborador = !ehAdmin(me.papel);
    let lista: PrazoCard[];
    if (souColaborador) {
      lista = todos.filter(
        (c) => c.etiqueta === 'verde' ||
               (c.protocolado_em && c.protocolado_em.startsWith('2026-05')),
      );
    } else {
      // admin
      lista = todos.filter(
        (c) =>
          c.etiqueta === 'vermelho' ||
          c.etiqueta === 'amarelo' ||
          c.atribuido_a?.id === me.id ||
          c.etiqueta === 'verde',
      );
    }
    if (filtro === 'acao_necessaria') {
      if (souColaborador)
        return lista.filter((c) => c.etiqueta === 'verde');
      return lista.filter(
        (c) =>
          c.etiqueta === 'vermelho' ||
          (c.atribuido_a?.id === me.id && c.etiqueta !== 'verde'),
      );
    }
    if (filtro === 'aguardando') {
      if (souColaborador) return [];
      return lista.filter(
        (c) => c.etiqueta === 'verde' || c.etiqueta === 'amarelo',
      );
    }
    if (filtro === 'concluidos') return lista.filter((c) => c.etiqueta === 'cinza');
    return lista;
  }, [todos, me, filtro]);

  const grupos = useMemo(() => {
    const g = {
      urgente:     { titulo: '🔴 Urgente (vence em ≤ 2 dias úteis)', cards: [] as PrazoCard[] },
      essaSemana:  { titulo: '🟡 Essa semana',                       cards: [] as PrazoCard[] },
      proxima:     { titulo: '📅 Próxima semana',                    cards: [] as PrazoCard[] },
      futuro:      { titulo: '📆 Mais à frente',                     cards: [] as PrazoCard[] },
      concluidos:  { titulo: '⚪ Concluídos',                        cards: [] as PrazoCard[] },
    };
    for (const c of relevantes.sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento)
      || a.numero_processo.localeCompare(b.numero_processo))) {
      if (c.etiqueta === 'cinza') { g.concluidos.cards.push(c); continue; }
      const d = c.dias_ate_vencimento;
      if (d <= 2) g.urgente.cards.push(c);
      else if (d <= 7) g.essaSemana.cards.push(c);
      else if (d <= 14) g.proxima.cards.push(c);
      else g.futuro.cards.push(c);
    }
    return g;
  }, [relevantes]);

  const filtroClasse = (f: Filtro) =>
    `px-3 py-1.5 rounded text-xs font-semibold transition-all ${
      filtro === f
        ? 'btn-glass-neuro-primary'
        : 'btn-glass-neuro'
    }`;

  return (
    <PageShell
      titulo={
        <div>
          <h1 className="text-xl font-semibold text-high-contrast">📥 Caixa de {me?.nome}</h1>
          <p className="text-sm text-meta-contrast mt-0.5">
            {relevantes.length} cards · {me?.papel}
          </p>
        </div>
      }
      acoes={
        <div className="flex items-center gap-1.5">
          <BuscaProcesso
            onSelect={(c) => setDetalheId(c.id_prazo)}
            dica="Clique para abrir o detalhe"
            larguraInput="w-36 sm:w-44"
          />
          <button className={filtroClasse('todos')} onClick={() => setFiltro('todos')}>Todos</button>
          <button className={filtroClasse('acao_necessaria')} onClick={() => setFiltro('acao_necessaria')}>Ação necessária</button>
          <button className={filtroClasse('aguardando')} onClick={() => setFiltro('aguardando')}>Aguardando</button>
          <button className={filtroClasse('concluidos')} onClick={() => setFiltro('concluidos')}>Concluídos</button>
        </div>
      }
    >
        <div className="glass-panel rounded-xl shadow-lg p-5">
          <div className="space-y-6">
            {Object.values(grupos).map((g) => {
              if (g.cards.length === 0) return null;
              return (
                <section key={g.titulo}>
                  <h3 className="text-sm font-semibold text-meta-contrast mb-2">
                    {g.titulo}{' '}
                    <span className="text-slate-400 dark:text-slate-500 font-normal">({g.cards.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {g.cards.map((c) => (
                      <CardPrazo
                        key={c.id_prazo}
                        card={c}
                        onClick={() => setDetalheId(c.id_prazo)}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
            {relevantes.length === 0 && (
              <p className="text-center text-gray-400 dark:text-gray-500 py-12">
                Nada por aqui. 🎉
              </p>
            )}
          </div>
        </div>

      {detalheId !== null && (
        <ModalDetalhe idPrazo={detalheId} onClose={() => setDetalheId(null)} />
      )}
    </PageShell>
  );
}
